import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import Dinero from 'dinero.js'
import { Box, Grid, Typography } from '@mui/material'
import { useParams } from 'react-router-dom'
import { useGetCustomerQuery } from 'services/customers'
import { useGetInboundTicketsQuery } from 'services/inboundTickets'
import { useGetOutboundTicketsQuery } from 'services/outboundTickets'
import useGlobalLoader from 'utils/custom-hooks/useGlobalLoader'
import InboundTicketsListView from 'components/generic/datagrid/InboundTicketsListView'
import OutboundTicketGrid from 'components/generic/datagrid/OutboundTicketGrid/OutboundTicketGrid'
import { TICKET_STATUSES, SORTABLE_IBT_FIELDS, CURRENCY_PREPENDS } from 'utils/constants'
import { setDecimalPrecision } from 'utils/helpers'
import { useCustomerHeader } from '../account/utils'

const defaultFilter = {
  sort: {
    field: SORTABLE_IBT_FIELDS.TICKET_ID,
    ascending: false,
  },
  search: '',
}

const CustomerTickets = () => {
  const { id: customerId } = useParams()

  const { currencyExchangeRatesFF } = useSelector(state => state.featureFlags?.variables || {})
  const { defaultCurrency, defaultYardUm } = useSelector(state => state.settings)
  const { showWeightDecimals } = useSelector(state => state.settings?.settings?.purchaseSettings?.inboundTickets || {})

  const [ibtFilter, setIbtFilter] = useState({
    ...defaultFilter,
    customerId,
  })

  const [obtFilter, setObtFilter] = useState({
    ...defaultFilter,
    customerId,
  })

  const { data: customerData, isLoading: isLoadingCustomerData } = useGetCustomerQuery(
    { customerId },
    { skip: !customerId },
  )

  const { data: inboundTickets, isFetching: isFetchingInboundTickets } = useGetInboundTicketsQuery(
    {
      page: 1,
      pageSize: 500,
      filter: JSON.stringify(ibtFilter),
    },
    { skip: !ibtFilter?.sort?.field },
  )

  const { data: outboundTickets, isFetching: isFetchingOutboundTickets } = useGetOutboundTicketsQuery(
    {
      page: 1,
      pageSize: 500,
      filter: JSON.stringify(obtFilter),
    },
    { skip: !obtFilter?.sort?.field },
  )

  useCustomerHeader(customerData)
  useGlobalLoader(isLoadingCustomerData)

  useEffect(() => {
    if (customerData && customerData?.isCarrier) {
      const newFilter = {
        ...defaultFilter,
        carrierId: customerId,
      }
      setIbtFilter(newFilter)
      setObtFilter(newFilter)
    }
  }, [customerData, customerId])

  const ibtRows = []
  const obtRows = []
  if (customerData?.isSupplier || customerData?.isCarrier) {
    inboundTickets?.Items?.forEach(ticket => {
      const ticketCurrency = ticket?.netValue?.currency
      const pricePrepend =
        !ticketCurrency || defaultCurrency === ticketCurrency
          ? CURRENCY_PREPENDS[defaultCurrency]?.hc
          : CURRENCY_PREPENDS[ticketCurrency]?.fc
      ibtRows.push({
        ...ticket,
        ticketLink: `/app/purchases/ticket-library/${ticket.id}`,
        ticketStatus: TICKET_STATUSES[ticket.status?.value]?.name,
        ticketEffectiveDate: ticket.effectiveDate ? new Date(ticket.effectiveDate) : undefined,
        customerIdentifierString: ticket.customer?.customerCommonIdentifierString,
        truckNumber: ticket.transportationInfo?.carrierNumber,
        trailerNumber: ticket.transportationInfo?.trailerNumber,
        billOfLading: ticket.transportationInfo?.billOfLading,
        grossWeight: `${setDecimalPrecision(ticket.loadWeight?.gross, {
          removeTrailingZeros: true,
          decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
        })} ${defaultYardUm}`,
        tareWeight: `${setDecimalPrecision(ticket.loadWeight?.tare, {
          removeTrailingZeros: true,
          decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
        })} ${defaultYardUm}`,
        netWeight: `${setDecimalPrecision(ticket.netWeight, {
          removeTrailingZeros: true,
          decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
        })} ${defaultYardUm}`,
        netValue: Dinero(ticket?.netValue).toUnit() + (ticket?.feeOrPromotionTotal || 0),
        pricePrepend: currencyExchangeRatesFF ? pricePrepend : '$',
      })
    })
  }
  if (customerData?.isConsumer || customerData?.isCarrier) {
    outboundTickets?.Items?.forEach(ticket => {
      obtRows.push({
        ...ticket,
        ticketId: isNaN(Number(ticket.tag)) ? ticket.tag : Number(ticket.tag),
        ticketStatus: ticket.status,
        ticketEffectiveDate: ticket.effectiveDate ? new Date(ticket.effectiveDate) : new Date(ticket.date),
        ticketShipDate: ticket.firstShipDate && new Date(ticket.firstShipDate),
        customerInfo: {
          name: ticket.customerDisplayName,
          address: `${ticket.shippingInfo.address}\n
                                    ${ticket.shippingInfo.city}, ${ticket.shippingInfo.state}\n
                                    ${ticket.shippingInfo.zip}`,
        },
        salesOrder: ticket.contractInfo?.salesOrder || '',
        custOrder: ticket.contractInfo?.customerPo || '',
        truckNumber: ticket.transportationInfo?.carrierNumber,
        trailerNumber: ticket.transportationInfo?.trailerNumber,
        container: ticket.transportationInfo?.container,
        materials: ticket.materialTypeCodes || [],
        materialTypeCommonNames: ticket.materialTypeCommonNames || [],
        netWeight: ticket.netWeight,
        grossWeight: ticket.grossWeight,
        tareWeight: ticket.tareWeight,
      })
    })
  }

  return (
    <Box p={3}>
      {(customerData?.isSupplier || customerData?.isCarrier) && (
        <Grid container rowSpacing={4}>
          <Grid item xs={12}>
            <Typography variant="h5">Inbound Ticket History</Typography>
          </Grid>
          <Grid item xs={12}>
            <InboundTicketsListView
              useBackendSortAndFilter={customerData?.isSupplier}
              filter={ibtFilter}
              setFilter={setIbtFilter}
              autoHeight
              rows={ibtRows}
              disableSelectionOnClick
              hideCustomerColumn
              loading={isFetchingInboundTickets}
            />
          </Grid>
        </Grid>
      )}
      {(customerData?.isConsumer || customerData?.isCarrier) && (
        <Grid container rowSpacing={4} pt={2}>
          <Grid item xs={12}>
            <Typography variant="h5">Outbound Ticket History</Typography>
          </Grid>
          <Grid item xs={12}>
            <OutboundTicketGrid
              filter={obtFilter}
              setFilter={setObtFilter}
              autoHeight
              rows={obtRows}
              disableSelectionOnClick
              hideCustomerColumn
              loading={isFetchingOutboundTickets}
            />
          </Grid>
        </Grid>
      )}
    </Box>
  )
}

export default CustomerTickets
